<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header simple -->
    <div class="bg-white border-b border-gray-200 px-6 py-4">
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-4">
          <button
            class="p-2 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100"
            @click="$router.go(-1)">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 class="text-xl font-semibold text-gray-900">Carte Interactive</h1>
        </div>
      </div>
    </div>

    <!-- Interface Split -->
    <div class="flex h-screen">
      <!-- Panneau Gauche (30%) -->
      <div class="w-1/3 bg-white border-r border-gray-200 flex flex-col">
        <!-- Header avec onglets -->
        <div class="p-4 border-b border-gray-200">
          <div class="flex space-x-1 bg-gray-100 rounded-lg p-1">
            <button v-for="tab in tabs" :key="tab.key" :class="[
              'flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors',
              activeTab === tab.key
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            ]" @click="setActiveTab(tab.key)">
              {{ tab.label }} ({{ getTabCount(tab.key) }})
            </button>
          </div>
        </div>

        <!-- Filtres -->
        <div class="p-4 border-b border-gray-200">
          <div class="space-y-3">
            <!-- Recherche -->
            <div class="relative">
              <input v-model="searchQuery" type="text" placeholder="Rechercher..."
                class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
              <svg class="absolute left-3 top-2.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor"
                viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>

            <!-- Filtres utilisateur -->
            <div class="flex space-x-2">
              <button :class="[
                'px-3 py-1 rounded-full text-sm font-medium transition-colors',
                filters.showMine
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-gray-100 text-gray-600'
              ]" @click="toggleFilter('mine')">
                Mes assets
              </button>
              <button :class="[
                'px-3 py-1 rounded-full text-sm font-medium transition-colors',
                filters.showPublic
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-600'
              ]" @click="toggleFilter('public')">
                Assets publics
              </button>
            </div>
          </div>
        </div>

        <!-- Compteur et actions -->
        <div class="p-4 border-b border-gray-200">
          <div class="flex items-center justify-between">
            <div class="text-sm text-gray-600">
              {{ visibleAssets.length }} élément{{ visibleAssets.length > 1 ? 's' : '' }}
              <span v-if="isViewFiltered" class="text-blue-600 font-medium">dans la vue</span>
            </div>
            <button class="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              @click="resetView">
              Voir tout
            </button>
          </div>
        </div>

        <!-- Liste des assets -->
        <div class="flex-1 overflow-y-auto">
          <div v-if="visibleAssets.length === 0" class="p-8 text-center text-gray-500">
            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.47-.881-6.08-2.33" />
            </svg>
            <p class="mt-2">Aucun élément trouvé</p>
            <p class="text-sm">{{ emptyStateMessage }}</p>
          </div>

          <div v-else class="divide-y divide-gray-200">
            <div v-for="asset in visibleAssets" :key="asset.uuid" :class="[
              'p-4 cursor-pointer hover:bg-gray-50 transition-colors',
              selectedAsset?.uuid === asset.uuid ? 'bg-blue-50' : ''
            ]" @click="centerOnAsset(asset)">
              <div class="flex items-start space-x-3">
                <div :class="[
                  'flex-shrink-0 w-3 h-3 rounded-full mt-1',
                  getAssetColor(asset.type)
                ]"></div>
                <div class="flex-1 min-w-0">
                  <h3 class="text-sm font-medium text-gray-900 truncate">
                    {{ asset.title }}
                  </h3>
                  <p class="text-xs text-gray-500 mt-1">
                    {{ formatCoordinates(asset.coordinates) }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Carte (70%) -->
      <div class="flex-1 relative">
        <div ref="mapContainer" class="w-full h-full"></div>

        <!-- Bouton Vue Globale -->
        <button v-if="isViewFiltered"
          class="absolute top-4 right-4 px-4 py-2 bg-white text-gray-700 rounded-lg shadow-lg hover:shadow-xl transition-shadow border border-gray-200 z-[1000]"
          @click="resetView">
          <svg class="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          Vue globale
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useMapLogic } from '@/composables/map/useMapLogic';
import type { AssetType, MapAsset } from '@/types/map';
import { computed, onMounted, ref, watch } from 'vue';

// Configuration des onglets
const tabs = [
  { key: 'location' as AssetType, label: 'Lieux' },
  { key: 'materialEntity' as AssetType, label: 'Entités' },
  { key: 'memo' as AssetType, label: 'MEMoS' }
];

// État local
const mapContainer = ref<HTMLElement | null>(null);
const activeTab = ref<AssetType>('location');
const searchQuery = ref('');
const selectedAsset = ref<MapAsset | null>(null);
const filters = ref({
  showMine: true,
  showPublic: true
});

// Logique métier déléguée au composable
const {
  assets,
  currentViewBounds,
  initializeMap,
  centerOnAsset: centerMapOnAsset,
  resetMapView,
  setMapAssets
} = useMapLogic();

// Assets filtrés
const filteredAssets = computed((): MapAsset[] => {
  let result = assets.value.filter(asset => asset.type === activeTab.value);

  // Filtre utilisateur
  if (!filters.value.showMine || !filters.value.showPublic) {
    result = result.filter(asset => {
      const isMine = asset.data.user_id === 'user123'; // TODO: Remplacer par l'utilisateur connecté
      return (filters.value.showMine && isMine) || (filters.value.showPublic && !isMine);
    });
  }

  // Filtre recherche
  if (searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase();
    result = result.filter(asset =>
      asset.title.toLowerCase().includes(query) ||
      (asset.data as { description?: string }).description?.toLowerCase().includes(query)
    );
  }

  return result;
});

// Assets visibles dans la vue actuelle
const visibleAssets = computed((): MapAsset[] => {
  if (!currentViewBounds.value) return filteredAssets.value;

  return filteredAssets.value.filter(asset => {
    const { latitude, longitude } = asset.coordinates;
    const bounds = currentViewBounds.value!;
    return latitude <= bounds.north &&
      latitude >= bounds.south &&
      longitude <= bounds.east &&
      longitude >= bounds.west;
  });
});

// État de la vue
const isViewFiltered = computed((): boolean => {
  return currentViewBounds.value !== null && visibleAssets.value.length < filteredAssets.value.length;
});

const emptyStateMessage = computed((): string => {
  if (isViewFiltered.value) {
    return 'Déplacez la carte ou cliquez sur "Voir tout"';
  }
  return 'Essayez de modifier vos filtres';
});

// Actions
const setActiveTab = (tab: AssetType): void => {
  activeTab.value = tab;
  selectedAsset.value = null;
};

const toggleFilter = (type: 'mine' | 'public'): void => {
  if (type === 'mine') {
    // Empêcher de désactiver les deux filtres
    if (!filters.value.showMine && !filters.value.showPublic) return;
    filters.value.showMine = !filters.value.showMine;
  } else {
    if (!filters.value.showMine && !filters.value.showPublic) return;
    filters.value.showPublic = !filters.value.showPublic;
  }
};

const centerOnAsset = (asset: MapAsset): void => {
  selectedAsset.value = asset;
  centerMapOnAsset(asset);
};

const resetView = (): void => {
  resetMapView();
  selectedAsset.value = null;
};

const getTabCount = (tabKey: AssetType): number => {
  return assets.value.filter(asset => asset.type === tabKey).length;
};

const getAssetColor = (type: AssetType): string => {
  const colors: Record<AssetType, string> = {
    location: 'bg-blue-500',
    materialEntity: 'bg-green-500',
    memo: 'bg-orange-500'
  };
  return colors[type];
};

const formatCoordinates = (coords: { latitude: number; longitude: number }): string => {
  return `${coords.latitude.toFixed(4)}, ${coords.longitude.toFixed(4)}`;
};

// Watchers
watch(filteredAssets, (newAssets) => {
  setMapAssets(newAssets);
}, { immediate: true });

// Lifecycle
onMounted(async () => {
  if (mapContainer.value) {
    await initializeMap(mapContainer.value);
  }
});
</script>

<style scoped>
/* Styles pour les clusters Leaflet */
:deep(.marker-cluster) {
  background-clip: padding-box;
  border-radius: 20px;
  font-weight: bold;
  text-align: center;
  color: white;
  font-size: 12px;
  line-height: 40px;
}

:deep(.marker-cluster div) {
  width: 30px;
  height: 30px;
  margin-left: 5px;
  margin-top: 5px;
  text-align: center;
  border-radius: 15px;
  font: 12px "Helvetica Neue", Arial, Helvetica, sans-serif;
}

:deep(.marker-cluster-small) {
  background-color: rgba(59, 130, 246, 0.8);
  /* Blue */
}

:deep(.marker-cluster-small div) {
  background-color: rgba(59, 130, 246, 0.9);
}

:deep(.marker-cluster-medium) {
  background-color: rgba(16, 185, 129, 0.8);
  /* Green */
}

:deep(.marker-cluster-medium div) {
  background-color: rgba(16, 185, 129, 0.9);
}

:deep(.marker-cluster-large) {
  background-color: rgba(245, 158, 11, 0.8);
  /* Orange */
}

:deep(.marker-cluster-large div) {
  background-color: rgba(245, 158, 11, 0.9);
}

/* Styles pour les tooltips et popups */
:deep(.custom-tooltip) {
  background: rgba(0, 0, 0, 0.8);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

:deep(.custom-popup) {
  z-index: 1000 !important;
}

:deep(.custom-popup .leaflet-popup-content-wrapper) {
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Styles pour les markers personnalisés */
:deep(.custom-marker) {
  background: transparent !important;
  border: none !important;
}

/* Assurer que la carte prend toute la hauteur */
:deep(.leaflet-container) {
  height: 100% !important;
  width: 100% !important;
}
</style>